<template>
  <VaDropdown
    v-model="isOpen"
    :offset="[13, 0]"
    class="notification-dropdown"
    stick-to-edges
    :close-on-content-click="false"
    @open="markAsRead"
  >
    <template #anchor>
      <VaButton preset="secondary" color="textPrimary">
        <VaBadge :text="unreadCount > 0 ? (unreadCount > 9 ? '9+' : String(unreadCount)) : ''" overlap>
          <VaIconNotification class="notification-dropdown__icon" />
        </VaBadge>
      </VaButton>
    </template>
    <VaDropdownContent class="h-full sm:max-w-[420px] sm:h-auto">
      <section class="p-4 overflow-auto">
        <p v-if="isLoading && items.length === 0" class="text-secondary text-sm py-2">Loading...</p>
        <p v-else-if="items.length === 0" class="text-secondary text-sm py-2">No recent activity</p>

        <VaList v-else class="space-y-1 mb-2">
          <template v-for="(item, index) in itemsWithRelativeTime" :key="item.id">
            <VaListItem class="notification-text">
              <VaListItemSection icon class="mx-0 p-0">
                <VaIcon
                  :name="eventIcon[item.eventType] ?? 'mso-info'"
                  :color="eventColor[item.eventType] ?? 'secondary'"
                />
              </VaListItemSection>
              <VaListItemSection>
                {{ item.description ?? eventLabel[item.eventType] ?? item.eventType }}
              </VaListItemSection>
              <VaListItemSection icon class="mx-1 whitespace-nowrap">
                {{ item.relativeTime }}
              </VaListItemSection>
            </VaListItem>
            <VaListSeparator v-if="index !== itemsWithRelativeTime.length - 1" class="mx-3" />
          </template>
        </VaList>

        <VaButton preset="primary" class="w-full" @click="goToActivityPage">View All</VaButton>
      </section>
    </VaDropdownContent>
  </VaDropdown>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import axios from 'axios'
import VaIconNotification from '../../../icons/VaIconNotification.vue'

interface ActivityLogItem {
  id: number
  eventType: string
  leadId: number | null
  jobNo: string | null
  transactionId: string | null
  merchantId: number | null
  description: string | null
  performedBy: string | null
  createdAt: string
}

const router = useRouter()
const isOpen = ref(false)
const isLoading = ref(false)
const items = ref<ActivityLogItem[]>([])

const LAST_SEEN_KEY = 'notifications:lastSeenAt'
const lastSeenAt = ref<number>(Number(localStorage.getItem(LAST_SEEN_KEY) ?? 0))

const eventIcon: Record<string, string> = {
  LeadCreated: 'mso-person_add',
  LeadUpdated: 'mso-edit',
  Capture: 'mso-payments',
  Void: 'mso-block',
  Refund: 'mso-currency_exchange',
  AutoCapture: 'mso-bolt',
  AutoCaptureFailed: 'mso-error',
}

const eventColor: Record<string, string> = {
  LeadCreated: 'success',
  LeadUpdated: 'info',
  Capture: 'primary',
  Void: 'warning',
  Refund: 'danger',
  AutoCapture: 'success',
  AutoCaptureFailed: 'danger',
}

const eventLabel: Record<string, string> = {
  LeadCreated: 'Lead created',
  LeadUpdated: 'Lead updated',
  Capture: 'Capture',
  Void: 'Void',
  Refund: 'Refund',
  AutoCapture: 'Auto-Capture',
  AutoCaptureFailed: 'Auto-Capture failed',
}

const unreadCount = computed(
  () => items.value.filter((item) => new Date(item.createdAt).getTime() > lastSeenAt.value).length,
)

const fetchNotifications = async () => {
  isLoading.value = true
  try {
    const { data } = await axios.get<ActivityLogItem[]>('/api/logs/activity', { params: { take: 15 } })
    items.value = data
  } finally {
    isLoading.value = false
  }
}

const markAsRead = () => {
  lastSeenAt.value = Date.now()
  localStorage.setItem(LAST_SEEN_KEY, String(lastSeenAt.value))
}

const goToActivityPage = () => {
  isOpen.value = false
  router.push({ name: 'activity' })
}

const rtf = new Intl.RelativeTimeFormat('en', { style: 'short' })
const TIME_NAMES = {
  second: 1000,
  minute: 1000 * 60,
  hour: 1000 * 60 * 60,
  day: 1000 * 60 * 60 * 24,
  week: 1000 * 60 * 60 * 24 * 7,
  month: 1000 * 60 * 60 * 24 * 30,
  year: 1000 * 60 * 60 * 24 * 365,
}

const getTimeName = (differenceTime: number) =>
  Object.keys(TIME_NAMES).reduce(
    (acc, key) => (TIME_NAMES[key as keyof typeof TIME_NAMES] < differenceTime ? key : acc),
    'second',
  ) as keyof typeof TIME_NAMES

const itemsWithRelativeTime = computed(() =>
  items.value.map((item) => {
    const diff = Math.max(0, Date.now() - new Date(item.createdAt).getTime())
    const timeName = getTimeName(diff)
    return {
      ...item,
      relativeTime: rtf.format(-Math.round(diff / TIME_NAMES[timeName]), timeName),
    }
  }),
)

let refreshInterval: ReturnType<typeof setInterval> | undefined

onMounted(() => {
  fetchNotifications()
  refreshInterval = setInterval(fetchNotifications, 60000)
})

onUnmounted(() => {
  clearInterval(refreshInterval)
})
</script>

<style lang="scss" scoped>
.notification-dropdown {
  cursor: pointer;

  .notification-dropdown__icon {
    position: relative;
    display: flex;
    align-items: center;
  }

  .va-dropdown__anchor {
    display: inline-block;
  }
}
</style>
